import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ConfirmDialog.css';

// Volcyy's own "are you sure?".
//
// window.confirm draws the operating system's alert — grey, unbranded, and on
// a phone close enough to a scam popup that people hesitate over it. This is
// the same question asked in the site's own voice.
//
// Used through useConfirm(), which keeps the call site reading like the
// window.confirm it replaced:
//
//   const [confirm, confirmDialog] = useConfirm();
//   if (!(await confirm({ title: 'Delete your review?' }))) return;
//   …
//   return (<>{confirmDialog}…</>);
export function ConfirmDialog({
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = true,
  onConfirm,
  onCancel,
}) {
  const cancelRef = useRef(null);
  const cardRef = useRef(null);
  const returnTo = useRef(null);

  useEffect(() => {
    returnTo.current = document.activeElement;
    // Cancel takes focus, not the destructive button — a stray Enter should
    // not be what deletes someone's review.
    cancelRef.current?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
        return;
      }
      // Keep tabbing inside the dialog while it is open.
      if (e.key !== 'Tab') return;
      const focusable = cardRef.current?.querySelectorAll('button');
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
      if (returnTo.current instanceof HTMLElement) returnTo.current.focus();
    };
  }, [onCancel]);

  return (
    <div className="confirm" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div
        className="confirm__card"
        ref={cardRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby={body ? 'confirm-body' : undefined}
      >
        <img src="/assets/logo.png" alt="" className="confirm__mark" aria-hidden="true" />
        <h2 className="confirm__title" id="confirm-title">{title}</h2>
        {body && <p className="confirm__body" id="confirm-body">{body}</p>}
        <div className="confirm__actions">
          <button type="button" className="confirm__btn confirm__btn--ghost" onClick={onCancel} ref={cancelRef}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`confirm__btn ${destructive ? 'confirm__btn--danger' : 'confirm__btn--primary'}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// Returns [confirm, dialog]. `confirm(options)` resolves true or false, so it
// drops straight into the place window.confirm used to sit.
export function useConfirm() {
  const [request, setRequest] = useState(null);

  const confirm = useCallback(
    (options) => new Promise((resolve) => setRequest({ ...options, resolve })),
    []
  );

  const settle = useCallback((answer) => {
    setRequest((current) => {
      current?.resolve(answer);
      return null;
    });
  }, []);

  const dialog = request ? (
    <ConfirmDialog
      title={request.title}
      body={request.body}
      confirmLabel={request.confirmLabel}
      cancelLabel={request.cancelLabel}
      destructive={request.destructive}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  ) : null;

  return [confirm, dialog];
}
